/**
 * Shared button styling constants for Crew Forge
 *
 * Consolidates button class strings that were previously duplicated
 * and inconsistent across pages. Same pattern as colors.js.
 *
 * Usage:
 *   import { BTN, BTN_XS, BTN_LG } from '../constants/buttons';
 *   <button className={BTN.primary}>Submit</button>
 *   <button className={`${BTN.confirm} ${isDirty ? 'animate-pulse' : ''}`}>Save</button>
 *
 * Categories NOT covered (stay custom):
 *   - Order action buttons (Approve, Reject, Mark Ordered, etc.) — semantic light-bg pattern
 *   - Filter/toggle pills (All/Actionable, status pills) — state-dependent groups
 *   - Icon-only utility (chevron, reorder arrows) — too specialized
 *   - Dashed "add item" button — unique visual pattern
 *   - Urgency quick-select toggles — dynamic color selection
 */

// ---------------------------------------------------------------------------
// Base classes shared by ALL button sizes and intents
// ---------------------------------------------------------------------------
const BASE = 'font-medium rounded-lg transition-colors inline-flex items-center gap-1.5 disabled:cursor-not-allowed';

// ---------------------------------------------------------------------------
// Size tokens
// ---------------------------------------------------------------------------
const SIZE = {
  xs: 'px-3 py-1 text-xs',
  sm: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

// ---------------------------------------------------------------------------
// Intent tokens — light + dark mode
// Solid intents use 70% opacity in dark mode for a glassy, integrated look.
// Hover bumps to 85%. Borders use /50 opacity in dark mode.
//
// 🎨 CONTRAST RULE FOR THE SOLID (white-text) INTENTS — 2026-10-01 pass.
// Each hue sits at the LIGHTEST Tailwind shade whose white text clears WCAG
// 1.4.3's 4.5:1, so all four land in one band (4.8–5.2:1 light) and read as
// equally weighted: blue-600, red-600, green-700, amber-700. Hover is one step
// darker, the border one step darker than the fill. Dark mode uses the SAME
// shade at /70 (/85 hover) — composited over the slate card #1e293b that is
// 5.9–7.3:1. Measured (white text, real composite):
//   intent   light  | dark /70  dark /85 hover   (was)
//   primary  5.17   | 7.11      6.07             (dark blue-500: hover 4.45 FAIL)
//   confirm  5.02   | 6.99      5.91             (green-600 3.30 / green-500 3.78 FAIL)
//   danger   4.83   | 7.28      5.91             (dark red-500: hover 4.67)
//   warning  5.02   | 7.19      5.96             (amber-500 2.15 / 3.61 FAIL)
// Pinned by constants/buttons.contrast.test.js — never step a fill LIGHTER
// without re-measuring; the -500/-600 "brand" shades fail on green and amber.
// ---------------------------------------------------------------------------
const INTENT = {
  primary:
    'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600/70 dark:hover:bg-blue-600/85 ' +
    'disabled:bg-blue-400 dark:disabled:bg-blue-600/30 ' +
    'text-white border border-blue-700 dark:border-blue-400/50 shadow-sm ' +
    'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',

  confirm:
    'bg-green-700 hover:bg-green-800 dark:bg-green-700/70 dark:hover:bg-green-700/85 ' +
    'disabled:bg-green-400 dark:disabled:bg-green-700/30 ' +
    'text-white border border-green-800 dark:border-green-400/50 shadow-sm ' +
    'focus:ring-2 focus:ring-green-500 focus:ring-offset-2',

  secondary:
    'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 ' +
    'text-gray-700 dark:text-gray-300 ' +
    'border border-gray-300 dark:border-gray-600 shadow-sm ' +
    'focus:ring-2 focus:ring-gray-400 focus:ring-offset-2',

  danger:
    'bg-red-600 hover:bg-red-700 dark:bg-red-600/70 dark:hover:bg-red-600/85 ' +
    'disabled:bg-red-400 dark:disabled:bg-red-600/30 ' +
    'text-white border border-red-700 dark:border-red-400/50 shadow-sm ' +
    'focus:ring-2 focus:ring-red-500 focus:ring-offset-2',

  'danger-soft':
    'bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 ' +
    'text-red-700 dark:text-red-300 ' +
    'border border-red-200 dark:border-red-800 shadow-sm ' +
    'focus:ring-2 focus:ring-red-400 focus:ring-offset-2',

  warning:
    'bg-amber-700 hover:bg-amber-800 dark:bg-amber-700/70 dark:hover:bg-amber-700/85 ' +
    'disabled:bg-amber-300 dark:disabled:bg-amber-700/30 ' +
    'text-white border border-amber-800 dark:border-amber-400/50 shadow-sm ' +
    'focus:ring-2 focus:ring-amber-400 focus:ring-offset-2',
};

// ---------------------------------------------------------------------------
// Pre-composed exports — size × intent
// ---------------------------------------------------------------------------
function compose(size) {
  const s = SIZE[size];
  const result = {};
  for (const [intent, classes] of Object.entries(INTENT)) {
    result[intent] = `${BASE} ${s} ${classes}`;
  }
  return result;
}

/** Standard size (px-4 py-2 text-sm) — most buttons */
export const BTN = compose('sm');

/** Extra-small (px-3 py-1 text-xs) — inline table actions */
export const BTN_XS = compose('xs');

/** Large (px-6 py-3 text-base) — page-level submit buttons */
export const BTN_LG = compose('lg');

/**
 * Dynamic helper for programmatic intent + size lookup.
 * @param {'primary'|'confirm'|'secondary'|'danger'|'danger-soft'|'warning'} intent
 * @param {'xs'|'sm'|'lg'} [size='sm']
 * @returns {string} Tailwind class string
 */
export function btn(intent, size = 'sm') {
  const sizeMap = { xs: BTN_XS, sm: BTN, lg: BTN_LG };
  return (sizeMap[size] || BTN)[intent] || BTN.primary;
}

/**
 * 📱 THE phone tap-target extender (mobile batch item 19, 2026-10-01): below
 * `md` an INVISIBLE `::after` hit box, at least 44×44 and never narrower than
 * the control, centred on it — the control LOOKS the same and catches a thumb.
 * Append it to a small icon/pill control's own classes. Everything is
 * `max-md:`, so desktop is byte-identical in effect.
 * 🪤 A pseudo-element is clipped for hit-testing by an `overflow-hidden`
 * ancestor and cannot exist on a `<select>`/`<input>` — use real `min-h`
 * there. Two controls closer than 44px apart overlap; give those real size.
 */
export const TAP_TARGET_PHONE =
  "max-md:relative max-md:after:absolute max-md:after:content-[''] max-md:after:left-1/2 max-md:after:top-1/2 " +
  'max-md:after:h-11 max-md:after:w-11 max-md:after:min-w-full max-md:after:-translate-x-1/2 max-md:after:-translate-y-1/2';
